/**
 * Login V2 — substitui `#login-modal` do V1 (delivery_app.html:1281+).
 *
 * Wizard de 5 passos no mesmo modal:
 *  1. `identifier`     — input do telefone OU email (máscara automática
 *                        em telefone numérico) → POST check-existence
 *  2a. `password`      — cliente já existe → senha + link "esqueci"
 *  2b. `register`      — cliente novo → nome + email/telefone extra +
 *                        (opcional) data de nascimento
 *  3a. `recover-channel` — recuperação: escolhe WhatsApp ou e-mail
 *  3b. `recover-confirm` — input do código OTP (4 dígitos) + nova senha
 *
 * Paridade V1 (não pular nenhuma validação — quem paga é o cliente final):
 *  - Telefone: normaliza "0" inicial, "55" leading, 10-13 dígitos
 *  - Email: regex `[^\s@]+@[^\s@]+\.[^\s@]+`, mín 5 chars
 *  - Nome: 3-100 caracteres
 *  - Senha: mín 4 chars (frontend; backend não enforce)
 *  - OTP: 4 dígitos, expira em 5 min
 *  - Conflito email/telefone no register → oferece login com identifier
 *  - Detecta `requireEmail`/`requireBirth` da loja (window.REQUIRE_*)
 *
 * Endpoints backend (auth.py):
 *  - POST /api/auth/customer/check-existence
 *  - POST /api/auth/customer/login-passwordless
 *  - POST /api/auth/customer/login                      (com senha)
 *  - POST /api/auth/customer/register
 *  - POST /api/auth/customer/request-otp                (recuperação)
 *  - POST /api/auth/customer/reset-password             (recuperação)
 *
 * Após sucesso (login OU passwordless OU register→passwordless):
 *  - Salva `wabiz_token` + `wabiz_user` no localStorage
 *  - Atualiza `window.USER`
 *  - Dispara `aliv:login-success` (CartBar/BottomNav/etc reagem)
 *  - Chama hooks V1 se disponíveis: linkConsentToUser, linkDeviceToUser,
 *    updateGreeting, updateCartUI, fetchUserHistory, initPushNotifications
 *  - Fecha a modal
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useModalOpen } from '../hooks/useAnyModalOpen';
import { track } from '../utils/tracking';
import { t, useI18n } from '../i18n';
import { motivoTelefoneInvalido } from '../../../shared/telefone';

declare global {
  interface Window {
    STORE_ID?: number | string;
    REQUIRE_EMAIL?: boolean;
    REQUIRE_BIRTH_DATE?: boolean;
    /** Hooks do V1 que reagem ao login (mantemos compat). */
    linkConsentToUser?: (userId: number) => void;
    linkDeviceToUser?: () => void;
    updateGreeting?: () => void;
    updateCartUI?: () => void;
    fetchUserHistory?: () => void;
    initPushNotifications?: () => void;
    /** Hook V1 antigo — interceptado pelo bootstrap. */
    toggleLoginModal?: () => void;
  }
}

interface LoggedInUser {
  id: number;
  name: string;
  phone: string;
  email?: string | null;
  addresses?: unknown[];
  points?: number;
}

type Step = 'identifier' | 'password' | 'otp' | 'register' | 'recover-channel' | 'recover-confirm';
type IdType = 'email' | 'phone';

// ─── Helpers ──────────────────────────────────────────────────────────

/** Máscara de telefone tipo `(11) 99999-9999` enquanto o usuário digita. */
function maskPhone(raw: string): string {
  let digits = raw.replace(/\D/g, '');
  // Autofill (Chrome e iOS 26.6+) preenche "+55 13 99704-0932". A máscara roda
  // no onChange, ANTES do normalizePhone do submit — então o corte em 11 já
  // tinha comido os 2 últimos dígitos e transformado o 55 em DDD quando o
  // normalizador olhava, e o guard `length > 11` dele nunca disparava. O
  // cliente caía em "não encontrado" e o OTP ia pra um número inexistente.
  // Mesmo bug que perdeu 2 leads na landing em 05/09/2026.
  if (digits.length > 11 && digits.startsWith('55')) digits = digits.slice(2);
  digits = digits.slice(0, 11);
  if (digits.length <= 2) return digits;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

/** Máscara de data DD/MM/AAAA. */
function maskDate(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

/** Normaliza telefone igual V1: remove caracteres, "0" inicial, "55" leading. */
function normalizePhone(raw: string): string {
  let digits = raw.replace(/\D/g, '');
  if (digits.startsWith('0')) digits = digits.slice(1);
  if (digits.startsWith('55') && digits.length > 11) digits = digits.slice(2);
  return digits;
}

function isEmailString(s: string): boolean {
  return s.includes('@');
}

function isValidEmail(s: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

/** Converte DD/MM/AAAA → YYYY-MM-DD pra payload do backend. */
function brDateToIso(br: string): string | null {
  const m = br.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const [, dd, mm, yyyy] = m;
  return `${yyyy}-${mm}-${dd}`;
}

function persistLogin(user: LoggedInUser, token: string | null) {
  try {
    if (token) localStorage.setItem('wabiz_token', token);
    localStorage.setItem('wabiz_user', JSON.stringify(user));
  } catch {
    /* quota / privacy mode */
  }
  // V1 mantém USER como `let` local — mas também espelhamos em window pra
  // V2 ler. Bootstrap já lê fallback localStorage, então é defensivo.
  try {
    window.USER = user as unknown as Window['USER'];
  } catch {
    /* */
  }
}

function fireLoginHooks(user: LoggedInUser) {
  try {
    window.linkConsentToUser?.(user.id);
  } catch (err) {
    console.warn('[v2 login] linkConsentToUser falhou', err);
  }
  try {
    window.linkDeviceToUser?.();
  } catch (err) {
    console.warn('[v2 login] linkDeviceToUser falhou', err);
  }
  try {
    window.updateGreeting?.();
  } catch (err) {
    console.warn('[v2 login] updateGreeting falhou', err);
  }
  try {
    window.updateCartUI?.();
  } catch (err) {
    console.warn('[v2 login] updateCartUI falhou', err);
  }
  try {
    window.fetchUserHistory?.();
  } catch (err) {
    console.warn('[v2 login] fetchUserHistory falhou', err);
  }
  // Dispara evento próprio pra ilhas V2 reagirem (CartBar, BottomNav etc).
  window.dispatchEvent(new CustomEvent('aliv:login-success', { detail: { user } }));
}

// ─── Main ─────────────────────────────────────────────────────────────

export function LoginIsland() {
  // assina a troca de idioma (ver i18n.ts)
  useI18n();
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState<Step>('identifier');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{ msg: string; kind: 'ok' | 'err' | 'info' } | null>(null);

  // Estado do wizard (preservado entre passos).
  const [rawId, setRawId] = useState(''); // O que o usuário digitou (com máscara se phone)
  const [idType, setIdType] = useState<IdType>('phone');
  const [cleanId, setCleanId] = useState(''); // Normalizado pro payload

  // Campos de cadastro
  const [regName, setRegName] = useState('');
  const [regExtraRaw, setRegExtraRaw] = useState(''); // Email se identifier=phone; phone se identifier=email
  const [regBirth, setRegBirth] = useState('');

  // Login com senha
  const [pwd, setPwd] = useState('');

  // Recovery
  const [recoveryChannel, setRecoveryChannel] = useState<'whatsapp' | 'email'>('whatsapp');
  const [otpCode, setOtpCode] = useState('');
  const [loginOtpVia, setLoginOtpVia] = useState<'whatsapp' | 'email'>('whatsapp');
  const [loginOtp, setLoginOtp] = useState('');
  const [newPwd, setNewPwd] = useState('');
  // Captura oportunista de e-mail: quando o cliente pediu recuperar por
  // e-mail mas não tem e-mail cadastrado, oferecemos cadastrar agora.
  // Envia código via WhatsApp (canal seguro) e grava o e-mail novo no
  // perfil junto com o reset (back valida + dedup).
  const [needsEmailCapture, setNeedsEmailCapture] = useState(false);
  const [newEmail, setNewEmail] = useState('');

  const inputRef = useRef<HTMLInputElement | null>(null);

  function flashToast(msg: string, kind: 'ok' | 'err' | 'info') {
    setToast({ msg, kind });
    window.setTimeout(() => setToast(null), 3500);
  }

  function resetWizard() {
    setStep('identifier');
    setRawId('');
    setIdType('phone');
    setCleanId('');
    setRegName('');
    setRegExtraRaw('');
    setRegBirth('');
    setPwd('');
    setRecoveryChannel('whatsapp');
    setOtpCode('');
    setNewPwd('');
    setNeedsEmailCapture(false);
    setNewEmail('');
    setBusy(false);
  }

  function close() {
    setVisible(false);
    // Reset suave — usuário pode reabrir limpo na próxima.
    setTimeout(resetWizard, 300);
  }

  /** Seta "voltar" do cabeçalho (e o voltar do celular fora da 1ª etapa). */
  function voltarEtapa() {
    if (step === 'otp') {
      setStep('identifier');
    } else if (step === 'recover-channel' || step === 'password') {
      setStep(step === 'recover-channel' ? 'password' : 'identifier');
    } else if (step === 'recover-confirm') {
      setStep('recover-channel');
    } else if (step === 'register') {
      setStep('identifier');
    }
  }

  useEffect(() => {
    function onOpen() {
      resetWizard();
      requestAnimationFrame(() => setVisible(true));
    }
    window.addEventListener('aliv:open-login-modal', onOpen);
    return () => window.removeEventListener('aliv:open-login-modal', onOpen);
  }, []);

  // Auto-focus no input principal de cada step.
  useEffect(() => {
    if (!visible) return;
    const t = window.setTimeout(() => inputRef.current?.focus(), 120);
    return () => window.clearTimeout(t);
  }, [visible, step]);

  // ESC fecha + lock scroll
  useEffect(() => {
    if (!visible) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [visible]);

  // Voltar do celular = a seta da tela; na 1ª etapa, fecha.
  useModalOpen(
    visible,
    () => (step === 'identifier' ? close() : voltarEtapa()),
    // profundidade = a da seta: recover-confirm → recover-channel → password → identifier
    { identifier: 1, password: 2, otp: 2, register: 2, 'recover-channel': 3, 'recover-confirm': 4 }[step],
  );

  // ─── Step 1: check existence ─────────────────────────────────────────
  async function submitIdentifier() {
    const rawTrim = rawId.trim();
    if (!rawTrim) {
      flashToast('Digite seu telefone ou e-mail.', 'err');
      return;
    }
    const isEmail = isEmailString(rawTrim);
    let id = rawTrim;
    if (isEmail) {
      if (id.length < 5) {
        flashToast('E-mail muito curto.', 'err');
        return;
      }
    } else {
      id = normalizePhone(rawTrim);
      if (id.length < 10 || id.length > 11) {
        flashToast('Celular inválido. Use DDD + número.', 'err');
        return;
      }
    }
    const type: IdType = isEmail ? 'email' : 'phone';
    setIdType(type);
    setCleanId(id);

    setBusy(true);
    try {
      const res = await fetch('/api/auth/customer/check-existence', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: id, type, store_id: Number(window.STORE_ID) || 0 }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        exists?: boolean;
        detail?: string;
      };
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);

      if (data.exists) {
        // Cliente existe → tenta passwordless direto. Se o backend NÃO
        // suportar passwordless pro modo da loja (ex: lojas exigem senha
        // configurada), cai pro `password` step com o detalhe do erro.
        await tryPasswordless(id);
      } else {
        // Conta nova: o número tem que existir antes de abrir o cadastro
        // (cliente antigo com número fora da régua continua entrando acima).
        const motivo = type === 'phone' ? motivoTelefoneInvalido(id) : null;
        if (motivo) {
          flashToast(motivo, 'err');
          return;
        }
        setStep('register');
      }
    } catch (err) {
      console.warn('[v2 login] check-existence erro', err);
      flashToast(err instanceof Error ? err.message : t('Erro ao verificar conta.'), 'err');
    } finally {
      setBusy(false);
    }
  }

  // ─── Login passwordless ──────────────────────────────────────────────
  async function tryPasswordless(identifier: string) {
    try {
      const res = await fetch('/api/auth/customer/login-passwordless', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          identifier,
          store_id: Number(window.STORE_ID) || 0,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        success?: boolean;
        otp_required?: boolean;
        via?: 'whatsapp' | 'email';
        access_token?: string;
        user?: LoggedInUser;
        detail?: string;
      };
      if (res.ok && data.otp_required) {
        // Loja com "Código por WhatsApp no login" ligado: o backend já mandou
        // o código; o cliente digita no próximo step.
        setLoginOtpVia(data.via || 'whatsapp');
        setLoginOtp('');
        setStep('otp');
        return;
      }
      if (res.status === 429 || res.status === 503) {
        flashToast(data.detail || t('Não foi possível enviar o código agora.'), 'err');
        return;
      }
      if (res.ok && data.success && data.user) {
        persistLogin(data.user, data.access_token || null);
        // GTM/Meta: login event (paridade V1 linha 11873/12118).
        track('login', { method: idType });
        flashToast(`Bem-vindo, ${data.user.name.split(' ')[0]}!`, 'ok');
        fireLoginHooks(data.user);
        close();
        // Push notification offer só roda após pequena pausa pra modal
        // sumir, paridade UX V1.
        setTimeout(() => {
          try {
            window.initPushNotifications?.();
          } catch {
            /* */
          }
        }, 1200);
        return;
      }
      // Backend pode requerer senha (passwordless desabilitado) — vai pro
      // step de senha sem tratar como erro.
      console.warn('[v2 login] passwordless rejeitado, indo pra senha:', data.detail);
      setStep('password');
    } catch (err) {
      console.warn('[v2 login] passwordless erro', err);
      setStep('password');
    }
  }

  // ─── Step 2b: código de login (loja com OTP ligado) ─────────────────
  async function submitLoginOtp() {
    const code = loginOtp.replace(/\D/g, '');
    if (code.length < 4) {
      flashToast(t('Digite o código de 4 dígitos.'), 'err');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch('/api/auth/customer/login-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: cleanId, code, store_id: Number(window.STORE_ID) || 0 }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        success?: boolean;
        access_token?: string;
        user?: LoggedInUser;
        detail?: string;
      };
      if (!res.ok || !data.success || !data.user) {
        throw new Error(data.detail || t('Código inválido ou expirado.'));
      }
      persistLogin(data.user, data.access_token || null);
      track('login', { method: idType });
      flashToast(`Bem-vindo, ${data.user.name.split(' ')[0]}!`, 'ok');
      fireLoginHooks(data.user);
      close();
      setTimeout(() => {
        try {
          window.initPushNotifications?.();
        } catch {
          /* */
        }
      }, 1200);
    } catch (err) {
      flashToast(err instanceof Error ? err.message : t('Código inválido.'), 'err');
    } finally {
      setBusy(false);
    }
  }

  async function resendLoginOtp() {
    setBusy(true);
    try {
      const res = await fetch('/api/auth/customer/request-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: cleanId, channel: loginOtpVia, store_id: Number(window.STORE_ID) || 0 }),
      });
      const data = (await res.json().catch(() => ({}))) as { success?: boolean; detail?: string; message?: string };
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      flashToast(data.message || t('Código reenviado.'), 'ok');
    } catch (err) {
      flashToast(err instanceof Error ? err.message : t('Não foi possível reenviar.'), 'err');
    } finally {
      setBusy(false);
    }
  }

  // ─── Step 2a: login com senha ───────────────────────────────────────
  async function submitPassword() {
    if (!pwd.trim()) {
      flashToast('Digite sua senha.', 'err');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch('/api/auth/customer/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          login_identifier: cleanId,
          password: pwd,
          store_id: Number(window.STORE_ID) || 0,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        success?: boolean;
        access_token?: string;
        user?: LoggedInUser;
        detail?: string;
      };
      if (!res.ok || !data.success || !data.user) {
        throw new Error(data.detail || 'Senha incorreta.');
      }
      persistLogin(data.user, data.access_token || null);
      // GTM/Meta: login event (paridade V1 linha 11873).
      track('login', { method: idType });
      flashToast(`Bem-vindo, ${data.user.name.split(' ')[0]}!`, 'ok');
      fireLoginHooks(data.user);
      close();
      setTimeout(() => {
        try {
          window.initPushNotifications?.();
        } catch {
          /* */
        }
      }, 1200);
    } catch (err) {
      console.warn('[v2 login] login erro', err);
      flashToast(err instanceof Error ? err.message : t('Erro ao entrar.'), 'err');
    } finally {
      setBusy(false);
    }
  }

  // ─── Step 2b: cadastro ──────────────────────────────────────────────
  async function submitRegister() {
    const name = regName.trim();
    if (name.length < 3 || name.length > 100) {
      flashToast('Nome deve ter entre 3 e 100 caracteres.', 'err');
      return;
    }

    const requireEmail = window.REQUIRE_EMAIL === true;
    const requireBirth = window.REQUIRE_BIRTH_DATE === true;
    let extraVal = regExtraRaw.trim();

    let phone = '';
    let email = '';

    if (idType === 'phone') {
      phone = cleanId;
      const motivo = motivoTelefoneInvalido(phone);
      if (motivo) {
        flashToast(motivo, 'err');
        return;
      }
      if (requireEmail && !extraVal) {
        flashToast('Informe seu e-mail.', 'err');
        return;
      }
      if (extraVal) {
        if (isValidEmail(extraVal)) {
          email = extraVal;
        } else if (requireEmail) {
          // Só bloqueia o submit quando a loja EXIGE e-mail. Quando o
          // campo é opcional, valor inválido (typo, autocomplete bugado)
          // é descartado silenciosamente — backend não recebe e cliente
          // entra sem e-mail. Não fica preso por causa de um campo
          // opcional.
          flashToast('E-mail inválido.', 'err');
          return;
        }
      }
    } else {
      email = cleanId;
      extraVal = normalizePhone(extraVal);
      const motivo = motivoTelefoneInvalido(extraVal);
      if (motivo) {
        flashToast(motivo, 'err');
        return;
      }
      phone = extraVal;
    }

    let birthIso: string | null = null;
    if (requireBirth) {
      if (regBirth.length !== 10) {
        flashToast('Data de nascimento obrigatória (DD/MM/AAAA).', 'err');
        return;
      }
    }
    if (regBirth.length === 10) {
      birthIso = brDateToIso(regBirth);
      if (!birthIso) {
        flashToast('Data inválida. Use DD/MM/AAAA.', 'err');
        return;
      }
    }

    setBusy(true);
    try {
      const payload: Record<string, unknown> = {
        name,
        phone,
        password: '',
        store_id: Number(window.STORE_ID) || 0,
      };
      if (email) payload.email = email;
      if (birthIso) payload.birth_date = birthIso;

      const res = await fetch('/api/auth/customer/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = (await res.json().catch(() => ({}))) as {
        success?: boolean;
        detail?: string;
        message?: string;
      };
      if (!res.ok || data.success === false) {
        // Detecção inteligente: já tem conta → oferece login com identifier
        // certo (mesma UX do V1).
        const lower = String(data.detail || '').toLowerCase();
        if (lower.includes('telefone já cadastrado')) {
          flashToast('Telefone já cadastrado. Entrando…', 'info');
          setRawId(maskPhone(phone));
          setCleanId(phone);
          setIdType('phone');
          setStep('identifier');
          // Re-dispara check pra ir pra passwordless/senha.
          setTimeout(submitIdentifier, 400);
          return;
        }
        if (lower.includes('e-mail') && lower.includes('cadastrado')) {
          flashToast('E-mail já cadastrado. Entrando…', 'info');
          setRawId(email);
          setCleanId(email);
          setIdType('email');
          setStep('identifier');
          setTimeout(submitIdentifier, 400);
          return;
        }
        throw new Error(data.detail || 'Erro ao criar conta.');
      }
      // Sucesso no cadastro → auto-login via passwordless.
      // GTM/Meta: sign_up event (paridade V1 linha 12004).
      track('sign_up', { method: idType });
      flashToast('Conta criada! Entrando…', 'ok');
      await tryPasswordless(idType === 'phone' ? phone : email);
    } catch (err) {
      console.warn('[v2 login] register erro', err);
      flashToast(err instanceof Error ? err.message : t('Erro ao criar conta.'), 'err');
    } finally {
      setBusy(false);
    }
  }

  // ─── Step 3a: pedir código OTP ───────────────────────────────────────
  async function requestRecoveryOtp(channel: 'whatsapp' | 'email') {
    setBusy(true);
    setRecoveryChannel(channel);
    try {
      const res = await fetch('/api/auth/customer/request-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          identifier: cleanId,
          channel,
          store_id: Number(window.STORE_ID) || 0,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        success?: boolean;
        message?: string;
        detail?: string;
      };
      if (!res.ok || data.success === false) {
        const msg = data.detail || data.message || '';
        // Cliente escolheu e-mail mas NÃO tem e-mail cadastrado: vira
        // oportunidade de captura. Abre input pro cliente digitar um
        // e-mail novo; o código continua indo pelo WhatsApp (canal já
        // verificado pelo cadastro), e o e-mail vai pro perfil quando
        // o reset for confirmado.
        if (
          channel === 'email' &&
          (msg.toLowerCase().includes('e-mail') || msg.toLowerCase().includes('email'))
        ) {
          setNeedsEmailCapture(true);
          setRecoveryChannel('whatsapp');
          flashToast(
            'Você ainda não tem e-mail. Cadastre um agora — o código vai pelo WhatsApp.',
            'info',
          );
          return;
        }
        throw new Error(msg || `HTTP ${res.status}`);
      }
      flashToast(data.message || 'Código enviado.', 'ok');
      setOtpCode('');
      setNewPwd('');
      setStep('recover-confirm');
    } catch (err) {
      console.warn('[v2 login] request-otp erro', err);
      flashToast(err instanceof Error ? err.message : t('Erro ao enviar código.'), 'err');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Variante do recover quando o cliente está cadastrando e-mail no
   * mesmo fluxo. Valida o e-mail antes de pedir o OTP via WhatsApp,
   * e guarda em `newEmail` pra mandar no reset-password.
   */
  async function requestRecoveryOtpWithNewEmail() {
    const em = newEmail.trim();
    if (!isValidEmail(em)) {
      flashToast('E-mail inválido.', 'err');
      return;
    }
    await requestRecoveryOtp('whatsapp');
  }

  // ─── Step 3b: confirmar nova senha ──────────────────────────────────
  async function submitNewPassword() {
    const code = otpCode.replace(/\D/g, '').slice(0, 6);
    if (code.length !== 4) {
      flashToast('Digite os 4 dígitos do código.', 'err');
      return;
    }
    if (newPwd.trim().length < 4) {
      flashToast('Crie uma senha com pelo menos 4 caracteres.', 'err');
      return;
    }
    setBusy(true);
    try {
      const payload: Record<string, unknown> = {
        identifier: cleanId,
        email: cleanId,
        code,
        new_password: newPwd,
        store_id: Number(window.STORE_ID) || 0,
      };
      // Captura oportunista — cliente digitou um e-mail novo no step
      // recover-channel; backend salva no perfil dele após o reset.
      if (needsEmailCapture && newEmail.trim()) {
        payload.new_email = newEmail.trim();
      }
      const res = await fetch('/api/auth/customer/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = (await res.json().catch(() => ({}))) as {
        success?: boolean;
        detail?: string;
      };
      if (!res.ok || data.success === false) {
        throw new Error(data.detail || 'Código inválido ou expirado.');
      }
      // Auto-login: cliente acabou de definir senha nova e validar OTP
      // (já provou que controla o WhatsApp). Não tem por que pedir pra
      // digitar a mesma senha que ele acabou de criar — login direto.
      flashToast('Senha alterada! Entrando…', 'ok');
      setOtpCode('');
      // Tenta passwordless primeiro (cobre lojas com login sem senha
      // habilitado). Se backend rejeitar, faz login com a senha nova.
      try {
        const passwordlessRes = await fetch('/api/auth/customer/login-passwordless', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            identifier: cleanId,
            store_id: Number(window.STORE_ID) || 0,
          }),
        });
        const pData = (await passwordlessRes.json().catch(() => ({}))) as {
          success?: boolean;
          access_token?: string;
          user?: LoggedInUser;
        };
        if (passwordlessRes.ok && pData.success && pData.user) {
          persistLogin(pData.user, pData.access_token || null);
          track('login', { method: idType });
          fireLoginHooks(pData.user);
          close();
          return;
        }
        // Backend não aceitou passwordless — tenta login com senha nova.
        const loginRes = await fetch('/api/auth/customer/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            login_identifier: cleanId,
            password: newPwd,
            store_id: Number(window.STORE_ID) || 0,
          }),
        });
        const lData = (await loginRes.json().catch(() => ({}))) as {
          success?: boolean;
          access_token?: string;
          user?: LoggedInUser;
        };
        if (loginRes.ok && lData.success && lData.user) {
          persistLogin(lData.user, lData.access_token || null);
          track('login', { method: idType });
          fireLoginHooks(lData.user);
          close();
          return;
        }
        // Fallback: se ambos falharem (raro), volta pro step de senha
        // pra cliente tentar manual.
        setPwd(newPwd);
        setStep('password');
      } catch (autoLoginErr) {
        console.warn('[v2 login] auto-login pós-reset falhou', autoLoginErr);
        setPwd(newPwd);
        setStep('password');
      }
    } catch (err) {
      console.warn('[v2 login] reset-password erro', err);
      flashToast(err instanceof Error ? err.message : t('Erro ao alterar senha.'), 'err');
    } finally {
      setBusy(false);
    }
  }

  // ─── Render ──────────────────────────────────────────────────────────

  const title = useMemo(() => {
    switch (step) {
      case 'identifier':
        return t('Entrar ou cadastrar');
      case 'password':
        return t('Sua senha');
      case 'otp':
        return t('Digite o código');
      case 'register':
        return t('Quase lá!');
      case 'recover-channel':
        return t('Recuperar senha');
      case 'recover-confirm':
        return t('Digite o código');
    }
  }, [step]);

  const subtitle = useMemo(() => {
    switch (step) {
      case 'identifier':
        return t('Informe seu celular ou e-mail');
      case 'password':
        return cleanId
          ? idType === 'phone'
            ? `Entrando como ${maskPhone(cleanId)}`
            : `Entrando como ${cleanId}`
          : '';
      case 'otp':
        return loginOtpVia === 'whatsapp'
          ? t('Enviamos um código de 4 dígitos no seu WhatsApp')
          : t('Enviamos um código de 4 dígitos no seu e-mail');
      case 'register':
        return t('Só algumas informações pra começar');
      case 'recover-channel':
        return t('Como você quer receber o código?');
      case 'recover-confirm':
        return `Enviamos um código de 4 dígitos via ${recoveryChannel === 'whatsapp' ? 'WhatsApp' : t('e-mail')}`;
    }
  }, [step, cleanId, idType, recoveryChannel]);

  if (!visible) return null;

  return createPortal(
    <div
      className={[
        'fixed inset-0 z-[82] flex items-end sm:items-center justify-center',
        'bg-black/60',
        'transition-opacity duration-200 ease-out',
        visible ? 'opacity-100' : 'opacity-0 pointer-events-none',
      ].join(' ')}
      onClick={close}
      role="dialog"
      aria-modal="true"
      aria-label={t("Entrar")}
    >
      <div
        className={[
          'relative w-full sm:w-[420px] sm:max-w-[420px]',
          'bg-white sm:rounded-2xl rounded-t-2xl',
          'shadow-[0_-8px_40px_rgba(0,0,0,0.25)] sm:shadow-2xl',
          'overflow-hidden max-h-[92vh] sm:max-h-[88vh]',
          'flex flex-col',
          'transition-transform duration-300',
          visible ? 'translate-y-0 sm:scale-100' : 'translate-y-full sm:translate-y-4 sm:scale-95',
        ].join(' ')}
        style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sm:hidden pt-2 pb-1 flex justify-center shrink-0 bg-white">
          <div className="w-10 h-1.5 rounded-full bg-gray-300" />
        </div>

        {/* Header */}
        <div className="px-5 pt-4 pb-3 border-b border-gray-100 flex items-start justify-between gap-3 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            {step !== 'identifier' && (
              <button
                type="button"
                onClick={voltarEtapa}
                aria-label={t("Voltar")}
                className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 flex items-center justify-center transition shrink-0"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <i className="fas fa-arrow-left text-sm" />
              </button>
            )}
            <div className="min-w-0">
              <h2 className="text-[15px] font-extrabold text-gray-900 leading-tight">{title}</h2>
              {subtitle && (
                <p className="text-[11px] text-gray-500 mt-0.5 leading-snug truncate">
                  {subtitle}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label={t("Fechar")}
            className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 flex items-center justify-center transition shrink-0"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <i className="fas fa-times text-sm" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 min-h-0 px-5 py-5 space-y-4">
          {step === 'identifier' && (
            <>
              <input
                ref={inputRef}
                type="text"
                value={rawId}
                onChange={(e) => {
                  const v = e.target.value;
                  // Se começa com letra OU já tem @ → trata como email cru;
                  // senão aplica máscara de telefone.
                  if (isEmailString(v) || /[a-zA-Z]/.test(v)) {
                    setRawId(v);
                  } else {
                    setRawId(maskPhone(v));
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitIdentifier();
                }}
                placeholder={t("(11) 99999-9999 ou email@exemplo.com")}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:border-red-500 outline-none"
                autoComplete="username"
              />
              <button
                type="button"
                onClick={submitIdentifier}
                disabled={busy}
                className="w-full bg-red-600 hover:bg-red-700 active:bg-red-800 text-white font-bold py-3 rounded-xl shadow-md active:scale-[0.98] transition flex items-center justify-center gap-2 text-sm disabled:opacity-60"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {busy ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-arrow-right" />}
                {t('Continuar')}
              </button>
            </>
          )}

          {step === 'password' && (
            <>
              <input
                ref={inputRef}
                type="password"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitPassword();
                }}
                placeholder={t("Sua senha")}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:border-red-500 outline-none"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={submitPassword}
                disabled={busy}
                className="w-full bg-red-600 hover:bg-red-700 active:bg-red-800 text-white font-bold py-3 rounded-xl shadow-md active:scale-[0.98] transition flex items-center justify-center gap-2 text-sm disabled:opacity-60"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {busy ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-sign-in-alt" />}
                {t('Entrar')}
              </button>
              <button
                type="button"
                onClick={() => setStep('recover-channel')}
                className="w-full text-xs font-bold text-red-600 hover:text-red-700 py-1"
              >
                {t('Esqueci minha senha')}
              </button>
            </>
          )}

          {step === 'otp' && (
            <>
              <input
                ref={inputRef}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={4}
                value={loginOtp}
                onChange={(e) => setLoginOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitLoginOtp();
                }}
                placeholder="0000"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-center text-2xl tracking-[0.5em] font-bold focus:border-red-500 outline-none"
              />
              <button
                type="button"
                onClick={submitLoginOtp}
                disabled={busy}
                className="w-full bg-red-600 hover:bg-red-700 active:bg-red-800 text-white font-bold py-3 rounded-xl shadow-md active:scale-[0.98] transition flex items-center justify-center gap-2 text-sm disabled:opacity-60"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {busy ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-sign-in-alt" />}
                {t('Entrar')}
              </button>
              <button
                type="button"
                onClick={resendLoginOtp}
                disabled={busy}
                className="w-full text-xs font-bold text-red-600 hover:text-red-700 py-1 disabled:opacity-60"
              >
                {t('Não recebi, reenviar código')}
              </button>
            </>
          )}

          {step === 'register' && (
            <>
              <div>
                <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">
                  {t('Nome completo')}
                </label>
                <input
                  ref={inputRef}
                  type="text"
                  value={regName}
                  onChange={(e) => setRegName(e.target.value)}
                  placeholder={t("Como podemos te chamar?")}
                  className="w-full mt-1 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:border-red-500 outline-none"
                  autoComplete="name"
                />
              </div>

              {/* Campo extra: ambos sempre visíveis.
                  - idType=phone → extra=email: obrigatório só se a loja
                    exigir (REQUIRE_EMAIL); senão "(opcional)" no label.
                  - idType=email → extra=telefone: SEMPRE obrigatório
                    (necessário pra entrega). */}
              <div>
                <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">
                  {idType === 'phone'
                    ? `E-mail${window.REQUIRE_EMAIL === true ? '' : ' (opcional)'}`
                    : t('Celular')}
                </label>
                <input
                  type={idType === 'phone' ? 'email' : 'tel'}
                  value={regExtraRaw}
                  onChange={(e) => {
                    if (idType === 'phone') {
                      setRegExtraRaw(e.target.value);
                    } else {
                      setRegExtraRaw(maskPhone(e.target.value));
                    }
                  }}
                  placeholder={idType === 'phone' ? t('email@exemplo.com') : '(11) 99999-9999'}
                  className="w-full mt-1 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:border-red-500 outline-none"
                  inputMode={idType === 'phone' ? 'email' : 'tel'}
                  autoComplete={idType === 'phone' ? 'email' : 'tel'}
                  maxLength={idType === 'phone' ? 100 : 15}
                />
              </div>

              {/* Data de nascimento: sempre visível, label muda pra
                  "(opcional)" quando a loja não exige. */}
              <div>
                <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">
                  Data de nascimento
                  {window.REQUIRE_BIRTH_DATE === true ? '' : ' (opcional)'}
                </label>
                <input
                  type="text"
                  value={regBirth}
                  onChange={(e) => setRegBirth(maskDate(e.target.value))}
                  placeholder={t("DD/MM/AAAA")}
                  inputMode="numeric"
                  maxLength={10}
                  className="w-full mt-1 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:border-red-500 outline-none tabular-nums"
                />
              </div>

              <button
                type="button"
                onClick={submitRegister}
                disabled={busy}
                className="w-full bg-red-600 hover:bg-red-700 active:bg-red-800 text-white font-bold py-3 rounded-xl shadow-md active:scale-[0.98] transition flex items-center justify-center gap-2 text-sm disabled:opacity-60"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {busy ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-user-plus" />}
                {t('Criar conta')}
              </button>
            </>
          )}

          {step === 'recover-channel' && (
            <>
              {needsEmailCapture ? (
                <>
                  <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
                    <p className="text-[12px] text-blue-800 leading-relaxed">
                      <i className="fas fa-info-circle mr-1" />
                      {t('Você ainda não tem e-mail cadastrado. Aproveite e cadastre agora — o código vai pelo seu WhatsApp e a gente salva o e-mail no seu perfil quando você confirmar a nova senha.')}
                    </p>
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">
                      {t('Seu e-mail')}
                    </label>
                    <input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder={t("email@exemplo.com")}
                      className="w-full mt-1 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:border-red-500 outline-none"
                      autoComplete="email"
                      inputMode="email"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={requestRecoveryOtpWithNewEmail}
                    disabled={busy}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 text-sm transition disabled:opacity-60"
                  >
                    {busy ? <i className="fas fa-spinner fa-spin" /> : <i className="fab fa-whatsapp text-base" />}
                    {t('Receber código pelo WhatsApp')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setNeedsEmailCapture(false);
                      setNewEmail('');
                    }}
                    className="w-full text-xs font-bold text-gray-500 hover:text-gray-700 py-1"
                  >
                    {t('Voltar')}
                  </button>
                </>
              ) : (
                <>
                  <p className="text-[12px] text-gray-600 leading-relaxed">
                    {t('Vamos enviar um código de 4 dígitos para você definir uma nova senha.')}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => requestRecoveryOtp('whatsapp')}
                      disabled={busy}
                      className="bg-green-600 hover:bg-green-700 text-white font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 text-sm transition disabled:opacity-60"
                    >
                      <i className="fab fa-whatsapp text-base" />
                      {t('WhatsApp')}
                    </button>
                    <button
                      type="button"
                      onClick={() => requestRecoveryOtp('email')}
                      disabled={busy}
                      className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 text-sm transition disabled:opacity-60"
                    >
                      <i className="far fa-envelope text-base" />
                      {t('E-mail')}
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          {step === 'recover-confirm' && (
            <>
              <input
                ref={inputRef}
                type="text"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric"
                maxLength={6}
                placeholder={t("Código recebido")}
                className="w-full border-2 border-gray-200 rounded-xl px-3 py-3 text-center text-2xl font-extrabold tabular-nums tracking-widest focus:border-red-500 outline-none"
              />
              <input
                type="password"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                placeholder={t("Nova senha (mín. 4 caracteres)")}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:border-red-500 outline-none"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={submitNewPassword}
                disabled={busy}
                className="w-full bg-red-600 hover:bg-red-700 active:bg-red-800 text-white font-bold py-3 rounded-xl shadow-md active:scale-[0.98] transition flex items-center justify-center gap-2 text-sm disabled:opacity-60"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {busy ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-check" />}
                {t('Confirmar nova senha')}
              </button>
              <button
                type="button"
                onClick={() => requestRecoveryOtp(recoveryChannel)}
                disabled={busy}
                className="w-full text-xs font-bold text-gray-500 hover:text-gray-700"
              >
                {t('Reenviar código')}
              </button>
            </>
          )}
        </div>

        {/* Toast */}
        {toast && (
          <div className="absolute left-3 right-3 bottom-3 z-[90]">
            <div
              className={[
                'rounded-xl px-4 py-3 text-sm font-bold shadow-lg text-white flex items-center gap-2',
                toast.kind === 'ok'
                  ? 'bg-green-600'
                  : toast.kind === 'info'
                  ? 'bg-gray-700'
                  : 'bg-red-600',
              ].join(' ')}
            >
              <i
                className={`fas ${
                  toast.kind === 'ok'
                    ? 'fa-check-circle'
                    : toast.kind === 'info'
                    ? 'fa-info-circle'
                    : 'fa-exclamation-circle'
                }`}
              />
              <span className="flex-1">{toast.msg}</span>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
